import { gql } from "@apollo/client";
import { useRefetchIfOld } from "components/data/ServerTime.js";
import useStdQuery from "components/data/hooks/useStdQuery.js";


export const RIDER_CLASSES = {
	fullFare: 1,
};

export const TRANSIT_ACCOUNT_FIELDS = gql`
	fragment TransitAccountFields on TransitAccount {
			id
			nodeTime
			subsystemAccountReference
			accountStatus
			accountStatusDescription
			accountStatusChangeDateTime
			riderClass
			riderClassDescription

			debtCollectionInfo {
				action
				amountDue

				#Per api doc: We get bankcardCharged only when "action" is "AvailableWait"
				bankcardCharged {
					maskedPan
					expirationMonth
					expirationYear
					creditCardType
				}
				attemptInSeconds
				remainingAttemptsForDay
			}

			tapVoidInfo {
				totalVoidCount
				provisionalVoidCount
				totalCorrectionCount
				provisionalCorrectionCount
			}

			orderInfo {
				totalVoidCount
				provisionalVoidCount
				totalCorrectionCount
				provisionalCorrectionCount
			}

			tokens {
				id
				tokenActivatedDateTime
				tokenId
				status
				statusDescription
				isArchived
				frontendStatus
				subsystemTokenType
				mediaType
				tokenLastUsageDetails {
					transactionDateTime
					location
				}
				tokenInfo {
					mobileWalletType
					mobileWalletDeviceType
					masterTokenPartial
					tokenNickname
					... on WSSubsystemAccountSmartcardTravelTokenDisplay {
						serialNumber
						cardExpiryDate
					}

					... on WSSubsystemAccountVirtualCardTravelTokenDisplay {
						maskedPan
						expirationMonth
						expirationYear
					}

					... on WSSubsystemAccountBankcardTravelTokenDisplay {
						maskedPan
						expirationMonth
						expirationYear
						creditCardType
					}

					tokenEnablementFee {
						status
						statusReasonCode
						statusLastUpdate
						feeDueAmount
					}
					tokenType
				}
				...on WSTransitAccountToken {
					canBePrimary
					statusReasonCodeDesc
					statusReasonCode
				}
			}

			purses {
				balance
				purseRestriction
				purseRestrictionDescription
				purseType
				purseTypeDescription
				supportsTransfer
			}

			passes {
				id
				passSKU
				createdDateTime
				travelTokenId
				passDescription
				expirationDateTime
				startDateTime
				endDateTime
				passSerialNbr
				supportsAutoload
				fareProductTypeId
				autoloadEnabled
				supportsTransfer
				firstUseDateTime
				passUseCount
				payments {
					paymentType
				}
			}

			subscriptions{
				id
				passId
				subscriptionId
				status
				startDtm
				criteria {
					type
					thresholdValue
					day
					frequency
					recurrence
				}
				autoload {
					type
					loadValueBehavior
					...on WSAutoloadSubsystemValue {
						subsystemRef
						subsystemAccountId
					}

					...on WSAutoloadSubsystemProduct {
						subsystemAccountId
						productName
						productSKU
						travelTokenId
					}
				}
			}

			# WSProgramMembership
			programMemberships {
				id
				b2bCustomerId
				b2bCustomerName
				programId
				programName
				memberId
				memberStatus
				memberStatusShortDesc
				# WSMemberBenefit
				benefitElections {
					id
					benefitId
					benefitName
					benefitElectionId
					benefitDeliveryTypeId
					pretax
					electedAmount
				}
				memberStartDate
				memberExpirationDate
				memberExternalRef
			}

			# WSTransitAccountTransferProductInfo
			transferProductInfo {
				totalTransferCount
				provisionalTransferCount
			}
	}
`;

// Fetch TA from transitaccount/<transitaccount-id>/subsystem/<subsystem-id> GET
export const GET_TRANSIT_ACCOUNT = gql`
	${TRANSIT_ACCOUNT_FIELDS}
	query TransitAccountGET (
		$subsystemAccountReference: ID
		$returnArchivedTokens: Boolean!
		$returnSubscriptions: Boolean!
		$returnProgramMemberships: Boolean!
		$returnTransferProductInfo: Boolean!
		$returnProductPayments: Boolean!
		$returnOrderInfo: Boolean!
		$returnTapVoidInfo: Boolean!
		$returnTapCorrectionInfo: Boolean!
	){
		transitAccountQ (
			subsystemAccountReference: $subsystemAccountReference
			returnProductPayments: $returnProductPayments
			returnOrderInfo: $returnOrderInfo
			returnTapVoidInfo: $returnTapVoidInfo
			returnTapCorrectionInfo: $returnTapCorrectionInfo
			returnTransferProductInfo: $returnTransferProductInfo
			returnSubscriptions: $returnSubscriptions
			returnProgramMemberships: $returnProgramMemberships
			returnArchivedTokens: $returnArchivedTokens
		){
			...TransitAccountFields
		}
	}
`;

// Fetch TA from transitaccount/<transitaccount-id>/subsystem/<subsystem-id> GET
export const GET_ALL_TRANSIT_ACCOUNTS = gql`
	${TRANSIT_ACCOUNT_FIELDS}
	query AllTransitAccountsGET (
		$returnArchivedTokens: Boolean!
		$returnSubscriptions: Boolean!
		$returnProgramMemberships: Boolean!
		$returnTransferProductInfo: Boolean!
		$returnProductPayments: Boolean!
		$returnOrderInfo: Boolean!
		$returnTapVoidInfo: Boolean!
		$returnTapCorrectionInfo: Boolean!
	){
		allTransitAccountsQ (
			returnProductPayments: $returnProductPayments
			returnOrderInfo: $returnOrderInfo
			returnTapVoidInfo: $returnTapVoidInfo
			returnTapCorrectionInfo: $returnTapCorrectionInfo
			returnTransferProductInfo: $returnTransferProductInfo
			returnSubscriptions: $returnSubscriptions
			returnProgramMemberships: $returnProgramMemberships
			returnArchivedTokens: $returnArchivedTokens
		){
			nodeTime
			transitAccounts {
				...TransitAccountFields
			}
		}
	}
`;


export const getTransitAccountDefaultRefetchVariables = (subsystemAccountReference) =>  ({
	subsystemAccountReference,
	returnArchivedTokens: false,
	returnSubscriptions: false,
	returnProgramMemberships: false,
	returnTransferProductInfo: false,
	returnProductPayments: false,
	returnOrderInfo: false,
	returnTapVoidInfo: false,
	returnTapCorrectionInfo: false,
});

export const getAllTransitAccountDefaultRefetchVariables = () =>  ({
	returnArchivedTokens: false,
	returnSubscriptions: false,
	returnProgramMemberships: false,
	returnTransferProductInfo: false,
	returnProductPayments: false,
	returnOrderInfo: false,
	returnTapVoidInfo: false,
	returnTapCorrectionInfo: false,
});

export const getAllTransitAccountRefetchQueries = () => [
	{
		query: GET_ALL_TRANSIT_ACCOUNTS,
		variables: { ...getAllTransitAccountDefaultRefetchVariables() },
	},
];

export const getTransitAccountRefetchQueries = (subsystemAccountReference) => [
	{
		query: GET_TRANSIT_ACCOUNT,
		variables: {
			...getTransitAccountDefaultRefetchVariables(subsystemAccountReference),
			returnSubscriptions: true,
			returnProgramMemberships: true,
		},
	},
	{
		query: GET_TRANSIT_ACCOUNT,
		 variables: {
			...getTransitAccountDefaultRefetchVariables(subsystemAccountReference),
			returnSubscriptions: true,
		},
	},
	{ query: GET_TRANSIT_ACCOUNT, variables: getTransitAccountDefaultRefetchVariables(subsystemAccountReference) },
];

/**
 * @param {?string} subsystemAccountReference
 * @param archivedTokens
 * @param subscriptions
 * @param programMemberships
 * @param returnTransferProductInfo
 * @param productPayments
 * @param restProps
 * @return {{data: any, response: {subscribeToMore<TSubscriptionData=any, TSubscriptionVariables=OperationVariables extends OperationVariables>(options: SubscribeToMoreOptions<any, TSubscriptionVariables, TSubscriptionData>): () => void, variables: OperationVariables | undefined, data: any, called: boolean, startPolling(pollInterval: number): void, networkStatus: NetworkStatus, parsedError: any|null|null, refetch(variables?: Partial<OperationVariables>): Promise<ApolloQueryResult<any>>, reobserve(newOptions?: Partial<WatchQueryOptions<OperationVariables, any>>, newNetworkStatus?: NetworkStatus): Promise<ApolloQueryResult<any>>, error?: ApolloError, loading: boolean, updateQuery<TVars=OperationVariables extends OperationVariables>(mapFn: (previousQueryResult: any, options: Pick<WatchQueryOptions<TVars, any>, "variables">) => any): void, fetchMore<TFetchData=any, TFetchVars=OperationVariables extends OperationVariables>(fetchMoreOptions: (FetchMoreQueryOptions<TFetchVars, TFetchData> & {updateQuery?: (previousQueryResult: any, options: {fetchMoreResult: TFetchData, variables: TFetchVars}) => any})): Promise<ApolloQueryResult<TFetchData>>, stopPolling(): void, observable: ObservableQuery<any, OperationVariables>, client: ApolloClient<any>, queryName: any, previousData?: any}, loading: boolean}}
 */
export const useTransitAccount = ({
	subsystemAccountReference = null,
	returnArchivedTokens = false,
	returnSubscriptions = false,
	returnProgramMemberships = false,
	returnTransferProductInfo = false,
	returnProductPayments = false,
	returnOrderInfo = false,
	returnTapVoidInfo = false,
	returnTapCorrectionInfo = false,
	...restProps
}) => {
	const response = useStdQuery(GET_TRANSIT_ACCOUNT, {
		variables: {
			subsystemAccountReference,
			returnOrderInfo,
			returnArchivedTokens,
			returnSubscriptions,
			returnProgramMemberships,
			returnTransferProductInfo,
			returnProductPayments,
			returnTapVoidInfo,
			returnTapCorrectionInfo,
		},
		...restProps,
	});

	const { data, loading: loadingResponse, error, refetch } = response;
	const queryTime = data?.transitAccountQ?.nodeTime;

	useRefetchIfOld({ refetch, loading: loadingResponse, queryTime });

	return { data, loading: loadingResponse, error, response };
};

export const useAllTransitAccounts = ({
	returnArchivedTokens = false,
	returnSubscriptions = false,
	returnProgramMemberships = false,
	returnTransferProductInfo = false,
	returnProductPayments = false,
	returnOrderInfo = false,
	returnTapVoidInfo = false,
	returnTapCorrectionInfo = false,
	...restProps
}) => {

	const response = useStdQuery(GET_ALL_TRANSIT_ACCOUNTS, {
		variables: {
			returnOrderInfo,
			returnArchivedTokens,
			returnSubscriptions,
			returnProgramMemberships,
			returnTransferProductInfo,
			returnProductPayments,
			returnTapVoidInfo,
			returnTapCorrectionInfo,
		},
		...restProps,
	});

	const { data, loading: loadingResponse, error } = response;

	return {
		transitAccounts: data?.allTransitAccountsQ?.transitAccounts ?? [],
		data,
		loading: loadingResponse,
		error,
		response,
	};
};
